import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, MapPin, Building2 } from "lucide-react";

const ROLES = [
  "Manufacturer",
  "Dealer / Lumberyard",
  "Distributor",
  "Sales Rep / Agency",
  "Contractor / Builder",
  "Other",
];

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    company_name: "",
    email: "",
    phone: "",
    role: ROLES[0],
    product_categories: "",
    dealer_network_size: "",
    website: "",
    message: "",
  });

  useEffect(() => {
    document.title = "Request a Demo — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Book a 30-minute demo of the LumberLoop USA demand intelligence platform for building materials manufacturers, dealers, and distributors.");
    window.scrollTo(0, 0);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim() || !form.company_name.trim() || !form.email.trim()) {
      toast.error("Name, company, and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/demo-request", form);
      setSubmitted(true);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="contact-success">
        <div className="w-16 h-16 mx-auto bg-[#C62828] text-white flex items-center justify-center">
          <CheckCircle2 size={32}/>
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-black uppercase mt-6 text-[#1A365D]">
          Request received.
        </h1>
        <p className="mt-4 text-lg text-slate-700 max-w-xl mx-auto">
          A member of the LumberLoop USA team will reach out within one business day to schedule your demo — usually the same day for manufacturers.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="btn-secondary">Return Home</Link>
          <Link to="/insights" className="btn-ghost">Read Building Insights</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="relative bg-white border-b border-slate-200">
        <div className="absolute inset-0 grid-backdrop opacity-40 pointer-events-none" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="label-tag">Request Early-Access Demo</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.02] text-[#17324D]">
            See how you&apos;ll <span className="text-[#C62828]">stop losing</span><br/>generated opportunities.
          </h1>
          <p className="mt-5 text-lg text-slate-700 max-w-3xl font-body">
            A brief walkthrough with your product categories and dealer footprint mapped in. No slides. Just the platform on your data.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 rounded-xl border border-slate-200 bg-white shadow-sm p-7" data-testid="demo-request-form">
          <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-slate-500">Book Your Demo</div>
          <h2 className="font-heading text-2xl md:text-3xl text-[#17324D] mt-1">Tell us about your business.</h2>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Your Name *">
              <input className="ll-input" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} data-testid="demo-name" required/>
            </Field>
            <Field label="Company *">
              <input className="ll-input" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="demo-company" required/>
            </Field>
            <Field label="Work Email *">
              <input type="email" className="ll-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="demo-email" required/>
            </Field>
            <Field label="Phone">
              <input className="ll-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="demo-phone"/>
            </Field>
            <Field label="You Are">
              <select className="ll-select" value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="demo-role">
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Website">
              <input className="ll-input" placeholder="https://..." value={form.website} onChange={(e) => update("website", e.target.value)} data-testid="demo-website"/>
            </Field>
            <Field label="Product Categories" sub="Roofing, siding, framing, etc.">
              <input className="ll-input" value={form.product_categories} onChange={(e) => update("product_categories", e.target.value)} data-testid="demo-categories"/>
            </Field>
            <Field label="Dealer Network Size" sub="Approx. # of dealers">
              <input className="ll-input" placeholder="50, 250, 1000+..." value={form.dealer_network_size} onChange={(e) => update("dealer_network_size", e.target.value)} data-testid="demo-network"/>
            </Field>
          </div>

          <Field label="What are you hoping to solve?" className="mt-4">
            <textarea className="ll-textarea" value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="demo-message" rows={4} placeholder="Optional — helps us tailor the demo."/>
          </Field>

          <button type="submit" className="btn-primary mt-6 inline-flex items-center gap-2 w-full sm:w-auto justify-center" disabled={submitting} data-testid="demo-submit">
            {submitting ? "Sending..." : <>Request Demo <ArrowRight size={16}/></>}
          </button>
          <p className="mt-3 text-xs text-slate-500">We reply within one business day. No sales pressure — just a walkthrough.</p>
        </form>

        <aside className="lg:col-span-2 space-y-5">
          <div className="rounded-xl border border-slate-200 p-5">
            <div className="w-11 h-11 bg-[#C62828] text-white flex items-center justify-center rounded-md"><Building2 size={20}/></div>
            <div className="font-heading text-lg text-[#17324D] mt-3">Purpose-Built for Manufacturers</div>
            <p className="text-sm text-slate-700 mt-2 font-body">Every feature designed around the manufacturer&apos;s command center — lead accountability, dealer performance, and channel intelligence.</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-5">
            <div className="w-11 h-11 bg-[#0F2540] text-white flex items-center justify-center rounded-md"><MapPin size={20}/></div>
            <div className="font-heading text-lg text-[#17324D] mt-3">American-Made</div>
            <p className="text-sm text-slate-700 mt-2 font-body">Built by industry professionals in the U.S. — for building product manufacturers who move American product.</p>
          </div>
          <div className="rounded-xl border border-slate-200 p-5">
            <div className="w-11 h-11 bg-[#17324D] text-white flex items-center justify-center rounded-md"><Mail size={20}/></div>
            <div className="font-heading text-lg text-[#17324D] mt-3">Prefer email?</div>
            <a href="mailto:info@lumberloopusa.com" className="text-[#C62828] font-bold hover:underline mt-1 inline-block" data-testid="contact-email-link">info@lumberloopusa.com</a>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, sub, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-mono-ll text-[10px] tracking-[0.25em] uppercase text-slate-600 mb-1">{label}</div>
      {sub && <div className="text-[11px] text-slate-500 mb-1.5 -mt-0.5">{sub}</div>}
      {children}
    </label>
  );
}
